import type { ArtworkPruefung } from "../../../../api/artworkPruefungen.ts";

/**
 * Die Fassung eines Artworks, auf die sich ein Prüfergebnis beziehen muss (Spec „Gültigkeit“): der Link, seine aktuelle
 * Version und die Position, an der er hängt. Jedes `PositionsDokument` (`api/dokumente.ts`) ist eine.
 */
export interface Fassung {
  linkId: string;
  jtlPosId: number;
  version: number;
}

/** Ein Prüfstand ohne Ergebnis für genau diese Fassung, im gegebenen Zustand. */
export const ohneErgebnis = (fassung: Fassung, zustand: ArtworkPruefung["zustand"]): ArtworkPruefung => ({
  linkId: fassung.linkId,
  jtlPosId: fassung.jtlPosId,
  version: fassung.version,
  zustand,
  gesamt: null,
  grund: null,
  ergebnis: null,
  geprueftAm: null,
  gesehenVon: null,
  gesehenAm: null,
});

/**
 * Der Prüfstand eines Artworks, zu dem der Server (noch) nichts geliefert hat: „nicht geprüft“. So öffnet das Abzeichen auch
 * dann Einzelheiten, in denen „Prüfen“ möglich ist, wenn das Laden des Prüfstands scheiterte.
 */
export const nichtGeprueft = (fassung: Fassung): ArtworkPruefung => ohneErgebnis(fassung, "nicht_geprueft");

/**
 * Der Prüfstand, wie die Anzeige ihn zeigt: der des Servers, oder `veraltet` - ein Stand, den nur das Frontend kennt (kein Feld
 * des Servers): die gezeigte Fassung ist überholt, und Nachfragen löst es nicht auf („Stand veraltet“).
 */
export type AnzeigePruefung = ArtworkPruefung & { veraltet?: true };

/**
 * Wie weit der Hook (`useArtworkPruefungen`) über die gezeigten Fassungen urteilen kann:
 * - `offen`: die Antwort auf genau diese Fassungen steht noch aus; der Stand davor gehört zu einer anderen Fassung.
 * - `aktuell`: die Antwort liegt vor (die Kette fragt womöglich weiter).
 * - `aufgegeben`: die Kette hat nach ihrer Obergrenze aufgehört, obwohl der Server noch hinterherhinkt.
 */
export type Urteil = "offen" | "aktuell" | "aufgegeben";

/** Wie ein Eintrag zur Fassung steht: der Server hinkt hinterher (ältere Version), ist voraus, oder nur die Position weicht ab. */
const abstand = (eintrag: ArtworkPruefung, fassung: Fassung): "gleich" | "hinkt" | "voraus" | "position" => {
  if (eintrag.version !== fassung.version) return eintrag.version < fassung.version ? "hinkt" : "voraus";
  return eintrag.jtlPosId === fassung.jtlPosId ? "gleich" : "position";
};

/**
 * Ein Ergebnis gilt nur für die geprüfte Fassung an der geprüften Position (Spec „Gültigkeit“). Eine neue Version und das
 * Umhängen behalten den `linkId`, reihen aber serverseitig eine neue Prüfung ein: ein Eintrag mit anderer Version oder
 * Position darf weder „Passt“ noch Knöpfe der alten Fassung zeigen. Was stattdessen steht, hängt vom `urteil` des Hooks ab:
 * - Der Eintrag läuft (der Server arbeitet), oder die Antwort auf die gezeigten Fassungen steht noch aus: „Prüfung läuft“;
 *   das bleibt auch nach einem gescheiterten Abruf so (die Meldung steht im Fehlerstapel).
 * - Der Server ist voraus oder nur die Position weicht ab: „Stand veraltet“ - die Fassung des Aufrufers ist überholt, Nachfragen
 *   hilft nicht.
 * - Der Server hinkt hinterher: „Prüfung läuft“, solange die Kette nachfragt; hat sie aufgegeben, „Stand veraltet“.
 * Ohne Eintrag: `undefined` (= „nicht geprüft“).
 *
 * Dieselbe Regel gilt überall, wo Prüfergebnisse gezählt oder gezeigt werden (Reiter Dokumente, Zusammenfassung des
 * Massenuploads); nur der Hook (`useArtworkPruefungen`) pollt, und nur solange ein Eintrag läuft oder der Server hinkt.
 */
export function pruefungDerFassung(
  eintrag: ArtworkPruefung | undefined,
  fassung: Fassung,
  urteil: Urteil,
): AnzeigePruefung | undefined {
  if (!eintrag) return undefined;
  const art = abstand(eintrag, fassung);
  if (art === "gleich") return eintrag;
  const endgueltig = eintrag.zustand !== "laeuft" && (art === "hinkt" ? urteil === "aufgegeben" : urteil !== "offen");
  return endgueltig ? { ...nichtGeprueft(fassung), veraltet: true } : ohneErgebnis(fassung, "laeuft");
}

/** Ob zu einer der Fassungen ein Eintrag mit älterer Version vorliegt: der Server hinkt, ein weiterer Abruf kann ihn einholen. */
export function serverHinkt(nachLink: ReadonlyMap<string, ArtworkPruefung>, fassungen: readonly Fassung[]): boolean {
  return fassungen.some((fassung) => {
    const eintrag = nachLink.get(fassung.linkId);
    return eintrag !== undefined && abstand(eintrag, fassung) === "hinkt";
  });
}

/**
 * Ob zu einer der Fassungen ein Eintrag steht, der nicht zu ihr passt (andere Version ODER andere Position) oder noch läuft. Für
 * den Start eines Laufs: bevor die erste Antwort da ist, kennt der Hook die Richtung nicht (hinkt der Server, ist er voraus, hat sich
 * nur die Position geändert, läuft die Prüfung noch?) und fragt vorsichtshalber nach, falls der erste Abruf scheitert
 * (`useArtworkPruefungen`). Ein laufender Eintrag zählt mit: die Kette davor hat ihn verfolgt, ein neuer Lauf (Schlüsselwechsel)
 * hat sie abgelöst.
 */
export function offenBeimStart(nachLink: ReadonlyMap<string, ArtworkPruefung>, fassungen: readonly Fassung[]): boolean {
  return fassungen.some((fassung) => {
    const eintrag = nachLink.get(fassung.linkId);
    return eintrag !== undefined && (eintrag.zustand === "laeuft" || abstand(eintrag, fassung) !== "gleich");
  });
}

/**
 * Der Ladeschlüssel des Hooks: ändert sich, sobald ein Artwork eine neue Version bekommt oder umgehängt wird. `auffrischung` ist
 * ein Zähler des Aufrufers, der einen Neustart des Laufs erzwingt, ohne dass sich eine Fassung ändert (der Reiter Dokumente zählt
 * so das Schließen des Massenupload-Dialogs: dessen Aktionen haben den Prüfstand verändert). Ohne Artwork bleibt der Schlüssel leer.
 */
export function fassungsSchluessel(fassungen: readonly Fassung[], auffrischung = 0): string {
  const schluessel = fassungen
    .map((fassung) => `${fassung.linkId}:${fassung.version}:${fassung.jtlPosId}`)
    .sort()
    .join(",");
  return schluessel === "" || auffrischung === 0 ? schluessel : `${schluessel}#${String(auffrischung)}`;
}
